import React, { useState } from "react";
import { Download, Share2, X, Loader2, Trash2, Forward } from "lucide-react";
import { ForwardDialog } from "./ForwardDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { saveFiles, saveToDevice } from "../lib/share";

/** Sticky action bar for multi-select of photos/videos: Save (n) · Share (n) · cancel.
 *  items: [{ url, filename, kind }] */
export function MediaSelectBar({ items, onCancel, onDelete, className = "" }) {
  const { t } = useApp();
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const n = items.length;
  const run = async (mode) => {
    if (!n) return;
    setBusy(mode);
    try {
      const r = mode === "save" ? await saveToDevice(items) : await saveFiles(items);
      if (r === "saved") toast.success(t("saved_to_gallery_n").replace("{n}", n));
      else if (r === "downloaded") toast.success(t("saved_to_device"));
      else if (r === false) toast.error(t("something_wrong"));
      if (r && r !== false && r !== "cancelled") onCancel();
    } finally { setBusy(null); }
  };
  return (
    <div className={`flex items-center gap-1.5 rounded-xl border bg-card/95 backdrop-blur shadow-lg px-2 py-1.5 ${className}`} data-testid="media-select-bar">
      <span className="text-xs font-semibold px-1.5 min-w-[2ch] flex-1" data-testid="media-select-count">{n} {t("selected")}</span>
      <button type="button" disabled={!n || !!busy} onClick={() => run("save")} aria-label={t("save")} title={t("save")} data-testid="media-select-save"
        className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50">
        {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      </button>
      <button type="button" disabled={!n || !!busy} onClick={() => run("share")} aria-label={t("share")} title={t("share")} data-testid="media-select-share"
        className="h-9 w-9 rounded-lg border flex items-center justify-center disabled:opacity-50">
        {busy === "share" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
      </button>
      <button type="button" disabled={!n || !!busy} onClick={() => setForwardOpen(true)} aria-label={t("send_to_chat")} title={t("send_to_chat")} data-testid="media-select-forward"
        className="h-9 w-9 rounded-lg border flex items-center justify-center disabled:opacity-50">
        <Forward className="h-4 w-4" />
      </button>
      <ForwardDialog open={forwardOpen} media={items.map((it) => ({ id: it.id }))} onClose={() => { setForwardOpen(false); }} />
      {onDelete && (
        <button type="button" disabled={!n || !!busy} onClick={() => setConfirm(true)} aria-label={t("delete")} data-testid="media-select-delete"
          className="h-9 w-9 rounded-lg text-destructive hover:bg-destructive/10 flex items-center justify-center disabled:opacity-50">
          {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </button>
      )}
      <button type="button" onClick={onCancel} aria-label={t("cancel")} data-testid="media-select-cancel" className="h-9 w-9 rounded-lg hover:bg-muted flex items-center justify-center">
        <X className="h-4 w-4" />
      </button>
      {onDelete && (
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent data-testid="media-select-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("delete_selected_title").replace("{n}", n)}</AlertDialogTitle>
              <AlertDialogDescription>{t("delete_selected_desc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="media-select-delete-confirm-btn"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async () => { setBusy("delete"); try { await onDelete(); onCancel(); } finally { setBusy(null); } }}
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

/** Round check overlay for a selectable tile */
export function SelectMark({ selected, index }) {
  return (
    <div className={`absolute inset-0 transition-colors ${selected ? "bg-primary/25" : "bg-black/10"}`} data-testid="media-select-mark">
      <div className={`absolute top-1.5 right-1.5 h-6 w-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold ${selected ? "bg-primary border-primary text-white" : "border-white/90 bg-black/30"}`}>
        {selected ? index : ""}
      </div>
    </div>
  );
}
